import type { ErrorEvent, StackFrame } from '@sentry/react';

function getErrorMessage(event: ErrorEvent): string {
  return event.exception?.values?.[0]?.value ?? event.message ?? '';
}

function getStackFrames(event: ErrorEvent): StackFrame[] {
  return event.exception?.values?.[0]?.stacktrace?.frames ?? [];
}

function getStackSignature(event: ErrorEvent): string {
  return getStackFrames(event)
    .map((frame) => `${frame.filename ?? ''} ${frame.function ?? ''}`)
    .join(' ');
}

export function filterSentryEvent(event: ErrorEvent): ErrorEvent | null {
  const errorMessage = getErrorMessage(event);

  // Browser/extension-injected script trying to use the Chrome extension messaging API.
  // Shows up as unhandled promise rejection on some iOS/Safari contexts. Not our code.
  if (/runtime\.sendMessage\(\).*Tab not found/i.test(errorMessage)) return null;

  // Amplitude Session Replay sometimes can't persist to IndexedDB in iOS in-app browsers
  // (e.g. Facebook/Instagram) or after a user clears site storage. Not a funnel bug.
  if (
    /Failed to store session replay events in IndexedDB/i.test(errorMessage) ||
    /Database deleted by request of the user/i.test(errorMessage)
  ) {
    return null;
  }

  // Facebook/Instagram iOS in-app browsers expose window.webkit as {} without
  // messageHandlers — third-party scripts probing for it throw here. Not our bug.
  if (/webkit\.messageHandlers/i.test(errorMessage)) return null;

  // Benign layout-thrash warning, not an actual error.
  if (/ResizeObserver loop/i.test(errorMessage)) return null;

  // Fetch / promise aborted because the user navigated away, switched tabs, or the
  // in-app browser (e.g. Facebook iOS) tore down the page. Not a product defect.
  const exceptionValues = event.exception?.values ?? [];
  const isAbort =
    /AbortError|^The operation was aborted\.?$/i.test(errorMessage) ||
    exceptionValues.some(
      (v) =>
        v.type === 'AbortError' ||
        /AbortError|The operation was aborted/i.test(v.value ?? '')
    );
  if (isAbort) return null;

  const stackFrames = getStackFrames(event);
  const stackFilenames = stackFrames.map((f) => f.filename ?? '').join(' ');
  const stackSignature = getStackSignature(event);

  // Facebook iOS in-app browser injects login-form keyboard logging that posts
  // to the native bridge via dispatchToBridge/postMessage. Some contexts reject
  // with InvalidAccessError (DOMException code 15). Meta's script — not our code.
  const isFacebookIabLoginBridgeError =
    (/InvalidAccessError|The object does not support the operation or argument/i.test(
      errorMessage
    ) ||
      exceptionValues.some((v) => v.type === 'InvalidAccessError')) &&
    /dispatchToBridge|logLoginFormFocused|sendPostMessage/i.test(stackSignature);
  if (isFacebookIabLoginBridgeError) return null;

  // Pinterest Tag (loaded via GTM) calls ct.pinterest.com — fails under ad blockers,
  // strict WebViews, or DNS blocks. Crashes in pinimg.com script, not app source.
  if (
    /Failed to fetch \(ct\.pinterest\.com\)/i.test(errorMessage) ||
    /ct\.pinterest\.com/i.test(errorMessage) ||
    /pinimg\.com\/ct\//i.test(stackFilenames)
  ) {
    return null;
  }

  // Android in-app browser bridge teardown (e.g. Facebook IAB) can emit this error
  // during beforeunload when the Java object backing keyboard logging is destroyed.
  const isAndroidIabBridgeTeardown =
    /Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone/i.test(
      errorMessage
    ) &&
    /iabjs:\/\/navigation_performance_logger_android/i.test(stackFilenames);
  if (isAndroidIabBridgeTeardown) return null;

  // Expected profile PUT 400s from email-capture (validation / client errors).
  // Conversion continues; these are not product defects.
  if (
    /Validation failed/i.test(errorMessage) ||
    /Profile update failed with status 4\d\d/i.test(errorMessage)
  ) {
    return null;
  }

  // Stripe Express Checkout race / double-callback noise in IAB WebViews.
  if (
    /Unexpected call to paymentFailed\(\)/i.test(errorMessage) ||
    /Unhandled expressCheckout Element confirm event/i.test(errorMessage)
  ) {
    return null;
  }

  // Amplitude / UTM helpers touching restricted storage in iframe / IAB contexts.
  if (
    /SecurityError:\s*The operation is insecure/i.test(errorMessage) ||
    (exceptionValues.some((v) => v.type === 'SecurityError') &&
      /utm|page-url-enrichment|amplitude/i.test(stackSignature))
  ) {
    return null;
  }

  // Meta pixel / GTM tagging host flakiness (ad blockers, IAB teardown).
  if (
    /Failed to fetch \(gtm-tagging-server|googletagmanager|gtm-tagging-server/i.test(
      errorMessage
    ) ||
    /TypeError: Load failed \(gtm-tagging-server/i.test(errorMessage) ||
    /454:\s*Handling is disabled/i.test(errorMessage)
  ) {
    return null;
  }

  // Broader Android Facebook IAB bridge teardown (any postMessage method).
  if (
    /Error invoking postMessage: Java (object is gone|exception was raised)/i.test(
      errorMessage
    )
  ) {
    return null;
  }

  // Some iOS in-app browser contexts inject globals that are not present in
  // our page runtime and throw at document root script execution.
  const isMissingInjectedGlobalG =
    /Can't find variable:\s*_G/i.test(errorMessage) &&
    stackFrames.some((frame) =>
      /^https:\/\/(walkmore|join)\.move-club\.co\/?$/i.test(frame.filename ?? '')
    );
  if (isMissingInjectedGlobalG) return null;

  // Errors from browser extensions injecting scripts into the page.
  const hasExtensionFrame = stackFrames.some((frame) =>
    /^(chrome-extension|moz-extension):\/\//i.test(frame.filename ?? '')
  );
  if (hasExtensionFrame) return null;

  return event;
}
